import * as React from 'react';

import type { AiCoach_RespondToUserMessageInput as AiCoachRespondToUserMessageInput } from '__generated__/graphql-types';

import type { Location } from 'js/lib/useRouter';

import {
  clearChatSession,
  dropMessages,
  sendMessage,
  setChatSessionId,
  setChatSessionStorageKey,
  setContinuousChatSessionId,
  setContinuousChatSessionStorageKey,
} from 'bundles/ai-coach-platform/components/patterns/chat/store/dispatcher';
import {
  getChatSessionStorageKey,
  getContinuousChatSessionStorageKey,
  persistentChatSessionId,
  persistentContinuousChatSessionId,
} from 'bundles/ai-coach-platform/components/patterns/chat/utils/sessionUtils';

type UseCoachTriggerMessageOptions = {
  newSessionOnClick?: boolean;
  onClick?: () => void;
  skipSessionUpdate?: boolean;
  enableContinuousChat?: boolean;
  enableTextStreaming?: boolean;
  location: Location;
} & Pick<
  AiCoachRespondToUserMessageInput,
  'additionalContext' | 'courseraContext' | 'config' | 'responseProcessingType'
>;

/**
 * A hook that handles sending a message to Coach. Handles session state updates and message sending.
 */
const useCoachTriggerMessage = (options: UseCoachTriggerMessageOptions) => {
  const {
    newSessionOnClick,
    onClick,
    skipSessionUpdate = false,
    enableContinuousChat = false,
    enableTextStreaming = false,
    additionalContext,
    courseraContext,
    config,
    responseProcessingType,
    location,
  } = options;

  return React.useCallback<({ message, label }: { message?: string; label: string }) => void>(
    ({ message, label }) => {
      if (newSessionOnClick) {
        dropMessages();
        clearChatSession();
      }

      if (onClick) {
        onClick();
      }

      if (!skipSessionUpdate) {
        if (enableContinuousChat) {
          // If continuous chat is being enabled outside of course context (no courseId or courseSlug from location),
          // a uniqueDataString should be provided as the third parameter to getContinuousChatSessionStorageKey
          const courseId = courseraContext?.courseId || undefined;
          const storageKey = getContinuousChatSessionStorageKey(location, courseId);
          setContinuousChatSessionStorageKey(storageKey);
          setContinuousChatSessionId(persistentContinuousChatSessionId.get(storageKey));
        } else {
          const storageKey = getChatSessionStorageKey(location);
          setChatSessionStorageKey(storageKey);
          setChatSessionId(persistentChatSessionId.get(storageKey));
        }
      }

      return sendMessage({
        input: {
          message: message ?? label,
          additionalContext,
          courseraContext,
          config,
          responseProcessingType,
        },
        enableTextStreaming,
        enableContinuousChat,
      });
    },
    [
      location,
      newSessionOnClick,
      onClick,
      skipSessionUpdate,
      enableContinuousChat,
      enableTextStreaming,
      additionalContext,
      courseraContext,
      config,
      responseProcessingType,
    ]
  );
};

export default useCoachTriggerMessage;
